import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* ============================================================
   ░ HOW TO READ THIS FILE (для Claude Code / dev) ░
   ✅ PRODUCTION — переноси как есть: этот файл целиком.
      Точка монтирования: export default function Screen({ api, role }).
      Импорт только из '@kit-v5' + react. Платформенный хром (шапка,
      рельса, ящик уведомлений) НЕ рендерится — его даёт платформа.
   🚧 PROTOTYPE-ONLY — не здесь: демо-данные и фейк-api живут в
      notify-history.fixtures.js (при переносе заменяются клиентом).
   ⚠ ОБРАТИ ВНИМАНИЕ:
      • «История» — личная лента ВСЕГО пришедшего, бессрочная.
        Очистка колокольчика сюда не достаёт: удаления здесь нет.
      • Двери в рельсе у экрана нет — вход только по ссылке из ящика.
      • Строка кликабельна целиком → api.open(target) — платформенный
        переход к месту действия (Н-5).
      • Н-1: видимые строки — через словарь (ключи nhist.*), общий движок
        Оболочки (i18n/i18n.ts): формы множественного числа и язык дают t().
      • Н-4 / Д-16: значок и тон источника — из паспорта продукта (NAV_META кита), тот же источник,
        что у ящика: вид строки объявления в ящике и в истории один.
      • Д-13: это режим ящика (NotifyDrawer historyView), не отдельный экран; фильтры — по ширине панели.
      • Н-3: api.getHistory({source, from, to, cursor}) → { items, next } — по 50, «Показать ещё».
      • Н-2: лента — исключение из правила d.m.Y H:i:s: день заголовком
        («Сегодня» / «Вчера» / дата), время строки — как в колокольчике.

   Перенос (наше): типы api/данных в сигнатуре ниже — единственная правка тела,
   разрешённая FRONT-PORT.md («минимальные типы в сигнатуре, логику не трогать»).
   ============================================================ */
import React from 'react';
import { Ico, Btn, Dropdown, DateRange, Progress, EmptyState, ErrorState, NotifyGroup, NAV_META, typograph as tg } from '@kit-v5';
import { t, useT, getLocale } from '../i18n/i18n';
// S — тонкая обёртка над t() общего движка: ключ приходит сюда литералом, а в t() —
// уже переменной, поэтому сторож словарей не путает вызов с «голым» t(ключ-с-формами).
function S(key, vars) {
    return t(key, vars);
}
// MONTH_KEYS — родительный падеж («января») для dayLabel; индекс = Date.getMonth().
const MONTH_KEYS = [
    "nhist.month.1", "nhist.month.2", "nhist.month.3", "nhist.month.4",
    "nhist.month.5", "nhist.month.6", "nhist.month.7", "nhist.month.8",
    "nhist.month.9", "nhist.month.10", "nhist.month.11", "nhist.month.12",
];
// formatCount — «{n} уведомление/-я/-й»: форма числа выбирает t() по count (plural.ts, CLDR).
export function formatCount(n) {
    return S("nhist.count", { n, count: n });
}
const T = (s) => (typeof s === "string" ? tg(s) : s);
const NAV_META_TYPED = NAV_META;
/* ── паспорт продукта (Н-4): значок и тон источника берутся из NAV_META ── */
const sourceIcon = (id) => (id && NAV_META_TYPED[id] && NAV_META_TYPED[id].icon) || Ico.bell;
const sourceTone = (id) => (id && NAV_META_TYPED[id] && NAV_META_TYPED[id].tone) || "neutral";
export function dayLabel(d, now) {
    const same = (a, b) => a.toDateString() === b.toDateString();
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    if (same(d, now))
        return S("nhist.day.today");
    if (same(d, y))
        return S("nhist.day.yesterday");
    return d.getDate() + " " + S(MONTH_KEYS[d.getMonth()]) + (d.getFullYear() !== now.getFullYear() ? " " + d.getFullYear() : "");
}
/* Внутри дня — стопки по продукту-источнику (тот же паттерн, что в колокольчике):
   «Чат · 10» свёрнут в одну строку, раскрывается по клику. Иначе день превращается
   в портянку из десятка одинаковых сообщений одного продукта. */
function groupBySource(items) {
    const out = [], idx = {};
    items.forEach((it) => {
        const key = it.sourceId || "platform";
        if (idx[key] == null) {
            idx[key] = out.length;
            out.push({ key, source: it.source, items: [] });
        }
        out[idx[key]].items.push(it);
    });
    return out;
}
function groupByDay(items) {
    const now = new Date();
    const out = [];
    items.slice().sort((a, b) => +new Date(b.ts) - +new Date(a.ts)).forEach((n) => {
        const d = new Date(n.ts);
        const key = d.toDateString();
        let g = out[out.length - 1];
        if (!g || g.key !== key) {
            g = { key, label: dayLabel(d, now), items: [] };
            out.push(g);
        }
        g.items.push(n);
    });
    return out;
}
export default function Screen({ api, role }) {
    useT(); // подписка на смену языка — перерисовывает экран после PUT /api/prefs
    const locale = getLocale(); // день-заголовок (dayLabel) пересчитывается по текущему языку
    const [sources, setSources] = React.useState([]);
    const [source, setSource] = React.useState("all");
    const [range, setRange] = React.useState({ from: "", to: "" });
    const [items, setItems] = React.useState([]);
    /* Н-3: сервер отдаёт страницами по 50 с курсором — { items, next } */
    const [next, setNext] = React.useState(null);
    const [moreSt, setMoreSt] = React.useState("idle"); // idle | loading | error
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(false);
    React.useEffect(() => { let live = true; api.getSources().then((s) => live && setSources(s)).catch(() => { }); return () => { live = false; }; }, [api]);
    const load = React.useCallback(() => {
        setLoading(true);
        setError(false);
        let live = true;
        api.getHistory({ source, from: range.from, to: range.to })
            .then((r) => { if (live) {
            setItems(r.items || []);
            setNext(r.next || null);
            setMoreSt("idle");
            setLoading(false);
        } })
            .catch(() => { if (live) {
            setError(true);
            setLoading(false);
        } });
        return () => { live = false; };
    }, [api, source, range.from, range.to]);
    React.useEffect(() => load(), [load]);
    const loadMore = () => {
        if (!next || moreSt === "loading")
            return;
        setMoreSt("loading");
        api.getHistory({ source, from: range.from, to: range.to, cursor: next })
            .then((r) => { setItems((xs) => xs.concat(r.items || [])); setNext(r.next || null); setMoreSt("idle"); })
            .catch(() => setMoreSt("error"));
    };
    const dirty = source !== "all" || !!range.from || !!range.to;
    const reset = () => { setSource("all"); setRange({ from: "", to: "" }); };
    const groups = React.useMemo(() => groupByDay(items), [items, locale]);
    const decorate = (n) => ({ ...n, icon: sourceIcon(n.sourceId), tone: sourceTone(n.sourceId) });
    /* Н-5: переход к месту действия — платформенный метод api.open(target) */
    const open = (n) => { if (api.open && n.target)
        api.open(n.target); };
    return (_jsxs("div", { className: "nhist-root", "data-screen-label": S("nhist.screen"), children: [_jsxs("div", { className: "nhist-filters", children: [_jsx(Dropdown, { value: source, onChange: setSource, placeholder: S("nhist.filter.all_sources"), options: [{ value: "all", label: S("nhist.filter.all_sources") }].concat(sources.map((s) => ({ value: s.id, label: s.label }))) }), _jsx(DateRange, { value: range, onChange: (v) => setRange({ from: v.from, to: v.to }), showTime: false, placeholder: S("nhist.filter.all_time") }), dirty && _jsx(Btn, { kind: "ghost", size: "sm", onClick: reset, children: S("nhist.filter.reset") })] }), !loading && !error && items.length > 0 && (_jsx("div", { className: "nhist-count", children: formatCount(items.length) })), _jsx("div", { className: "nhist-body", children: loading ? (_jsx("div", { className: "nhist-load", children: _jsx(Progress, { indeterminate: true }) })) : error ? (_jsx(ErrorState, { message: S("nhist.error"), onRetry: load })) : !items.length ? (_jsx(EmptyState, { icon: Ico.history, title: S(dirty ? "nhist.empty.filtered.title" : "nhist.empty.title"), description: S(dirty ? "nhist.empty.filtered.desc" : "nhist.empty.desc"), action: dirty ? _jsx(Btn, { kind: "ghost", onClick: reset, children: S("nhist.empty.reset") }) : undefined })) : (_jsxs("div", { className: "nhist-list", children: [groups.map((g) => (_jsxs("section", { className: "nhist-day", children: [_jsx("div", { className: "nhist-day-head", children: T(g.label) }), _jsx("div", { className: "nhist-day-rows", children: groupBySource(g.items).map((s) => (_jsx(NotifyGroup, { source: s.source, items: s.items.map(decorate), onOpen: open, leaving: () => false }, s.key))) })] }, g.key))), next && (_jsx("div", { className: "nhist-more", children: _jsx(Btn, { kind: "ghost", size: "sm", disabled: moreSt === "loading", onClick: loadMore, children: S(moreSt === "loading" ? "nhist.more_loading" : moreSt === "error" ? "nhist.more_error" : "nhist.more") }) }))] })) })] }));
}
